import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileText, Download, Trash2, Plus, GripVertical, Calculator,
  ChevronUp, ChevronDown, Settings, Users, ClipboardList, Pencil, FileSpreadsheet,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const CalculadoraOrcamento = ({ getAuthHeaders }) => {
  const [dubladores, setDubladores] = useState([]);
  const [novoNome, setNovoNome] = useState("");
  const [novasVariacoes, setNovasVariacoes] = useState("");
  const [baseCalculo, setBaseCalculo] = useState(1.0);
  const [entradaTexto, setEntradaTexto] = useState("");
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDublador, setEditingDublador] = useState(null);
  const [editNome, setEditNome] = useState("");
  const [editVariacoes, setEditVariacoes] = useState("");
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState("substituir");
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);

  const headers = getAuthHeaders();

  const carregarDados = useCallback(async () => {
    try {
      const [dRes, cRes] = await Promise.all([
        axios.get(`${API}/tools/calc/dubladores`, { headers }),
        axios.get(`${API}/tools/calc/configuracao`, { headers })
      ]);
      setDubladores(dRes.data);
      setBaseCalculo(cRes.data.base_calculo);
    } catch (err) { console.error("Erro:", err); }
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const adicionarDublador = async () => {
    if (!novoNome.trim()) return toast.error("Digite um nome");
    try {
      const variacoes = novasVariacoes.split(',').map(v => v.trim()).filter(v => v);
      const res = await axios.post(`${API}/tools/calc/dubladores`, { nome: novoNome, variacoes }, { headers });
      setDubladores([...dubladores, res.data]);
      setNovoNome(""); setNovasVariacoes("");
      toast.success("Dublador adicionado!");
    } catch { toast.error("Erro ao adicionar"); }
  };

  const removerDublador = async (id) => {
    try {
      await axios.delete(`${API}/tools/calc/dubladores/${id}`, { headers });
      setDubladores(dubladores.filter(d => d.id !== id));
      toast.success("Removido!");
    } catch { toast.error("Erro ao remover"); }
  };

  const abrirEdicao = (d) => {
    setEditingDublador(d);
    setEditNome(d.nome);
    setEditVariacoes((d.variacoes || []).join(", "));
    setEditDialogOpen(true);
  };

  const salvarEdicao = async () => {
    if (!editNome.trim()) return toast.error("Digite um nome");
    try {
      const variacoes = editVariacoes.split(',').map(v => v.trim()).filter(v => v);
      const res = await axios.put(`${API}/tools/calc/dubladores/${editingDublador.id}`, { nome: editNome, variacoes }, { headers });
      setDubladores(dubladores.map(d => d.id === editingDublador.id ? res.data : d));
      setEditDialogOpen(false);
      toast.success("Atualizado!");
    } catch { toast.error("Erro ao atualizar"); }
  };

  const moverDublador = async (index, direcao) => {
    const novoIndex = index + direcao;
    if (novoIndex < 0 || novoIndex >= dubladores.length) return;
    const novos = [...dubladores];
    [novos[index], novos[novoIndex]] = [novos[novoIndex], novos[index]];
    setDubladores(novos);
    try {
      await axios.put(`${API}/tools/calc/dubladores/reorder`, { ids: novos.map(d => d.id) }, { headers });
    } catch { toast.error("Erro ao reordenar"); carregarDados(); }
  };

  const downloadLista = async () => {
    try {
      const res = await axios.get(`${API}/tools/calc/dubladores/exportar`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = 'lista_dubladores.xlsx';
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Lista exportada!");
    } catch { toast.error("Erro ao exportar"); }
  };

  const handleFileSelect = (e) => { const f = e.target.files[0]; if (f) { setImportFile(f); setImportDialogOpen(true); } };

  const importarLista = async () => {
    if (!importFile) return toast.error("Selecione um arquivo");
    try {
      const fd = new FormData();
      fd.append('file', importFile); fd.append('modo', importMode);
      const res = await axios.post(`${API}/tools/calc/dubladores/importar`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      setImportDialogOpen(false); setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      carregarDados();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao importar"); }
  };

  const atualizarBase = async (val) => {
    const v = parseFloat(val);
    if (isNaN(v) || v <= 0) return;
    setBaseCalculo(v);
    try { await axios.put(`${API}/tools/calc/configuracao`, { base_calculo: v }, { headers }); toast.success("Base atualizada!"); }
    catch { toast.error("Erro ao atualizar"); }
  };

  const calcular = async () => {
    if (!entradaTexto.trim()) return toast.error("Cole a lista de falas");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tools/calc/calcular`, { texto: entradaTexto, base_calculo: baseCalculo }, { headers });
      setResultados(res.data);
      toast.success("Calculado!");
    } catch { toast.error("Erro ao calcular"); }
    finally { setLoading(false); }
  };

  const exportarExcel = async () => {
    if (!resultados) return toast.error("Calcule primeiro");
    try {
      const res = await axios.post(`${API}/tools/calc/exportar/xlsx`, { resultados: resultados.dubladores }, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.download = 'orcamento.xlsx';
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel exportado!");
    } catch { toast.error("Erro ao exportar"); }
  };

  const exportarCSV = async () => {
    if (!resultados) return toast.error("Calcule primeiro");
    try {
      const res = await axios.post(`${API}/tools/calc/exportar/csv`, { resultados: resultados.dubladores }, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url; link.download = 'orcamento.csv';
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("CSV exportado!");
    } catch { toast.error("Erro ao exportar"); }
  };

  return (
    <div className="space-y-6" data-testid="calculadora-orcamento">
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Editar Dublador</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Nome Principal</label>
              <Input value={editNome} onChange={(e) => setEditNome(e.target.value)} className="bg-slate-900 border-slate-600 text-white" data-testid="calc-edit-nome" />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Variações (vírgula)</label>
              <Input value={editVariacoes} onChange={(e) => setEditVariacoes(e.target.value)} className="bg-slate-900 border-slate-600 text-white" data-testid="calc-edit-variacoes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={salvarEdicao} className="bg-teal-600 hover:bg-teal-700" data-testid="calc-save-edit">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(o) => { setImportDialogOpen(o); if (!o) { setImportFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; } }}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Importar Lista</DialogTitle>
            <DialogDescription className="text-slate-400">Arquivo: {importFile?.name || "Nenhum"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" value="substituir" checked={importMode === "substituir"} onChange={(e) => setImportMode(e.target.value)} className="accent-teal-500" />
                <span className="text-white text-sm">Substituir lista</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" value="adicionar" checked={importMode === "adicionar"} onChange={(e) => setImportMode(e.target.value)} className="accent-teal-500" />
                <span className="text-white text-sm">Adicionar à lista</span>
              </label>
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-xs text-slate-400">
              <p className="font-medium mb-1">Formato esperado (.xlsx):</p>
              <p>Coluna A: ORDEM | Coluna B: NOME | Coluna C: VARIAÇÕES</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={importarLista} className="bg-teal-600 hover:bg-teal-700" data-testid="calc-confirm-import">Importar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left - Config + List */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4"><Settings className="w-5 h-5 text-teal-400" /><span className="text-white font-medium">Configuração</span></div>
            <label className="text-sm text-slate-400 block mb-2">Base de Cálculo (R$ por fala)</label>
            <Input type="number" step="0.01" min="0.01" value={baseCalculo} onChange={(e) => setBaseCalculo(parseFloat(e.target.value) || 0)}
              onBlur={(e) => atualizarBase(e.target.value)} className="bg-slate-900 border-slate-600 text-white font-mono" data-testid="calc-base-input" />
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Users className="w-5 h-5 text-teal-400" /><span className="text-white font-medium">Dubladores</span></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={downloadLista} className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8 px-2 text-xs" data-testid="calc-export-list">
                  <Download className="w-3 h-3 mr-1" /> Exportar
                </Button>
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8 px-2 text-xs" data-testid="calc-import-list">
                  <Upload className="w-3 h-3 mr-1" /> Importar
                </Button>
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx" className="hidden" />
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex gap-2">
                <Input placeholder="Nome do dublador..." value={novoNome} onChange={(e) => setNovoNome(e.target.value)} className="bg-slate-900 border-slate-600 text-white flex-1" data-testid="calc-novo-nome" />
                <Button onClick={adicionarDublador} className="bg-teal-600 hover:bg-teal-700 px-3" data-testid="calc-add-btn"><Plus className="w-4 h-4" /></Button>
              </div>
              <Input placeholder="Variações (ex: Amanda L., Amanda Lampert)" value={novasVariacoes} onChange={(e) => setNovasVariacoes(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && adicionarDublador()} className="bg-slate-900 border-slate-600 text-white text-sm" data-testid="calc-novas-variacoes" />
            </div>

            <div className="border-t border-slate-700 pt-3">
              <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
                {dubladores.length === 0 ? (
                  <p className="text-center text-slate-500 py-6 text-sm">Nenhum dublador cadastrado</p>
                ) : dubladores.map((d, i) => (
                  <div key={d.id} className="flex items-center gap-2 bg-slate-900/60 border border-slate-700/50 rounded-lg p-2.5" data-testid={`calc-dub-${i}`}>
                    <GripVertical className="w-4 h-4 text-slate-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-white block truncate">{d.nome}</span>
                      {d.variacoes?.length > 0 && <span className="text-xs text-slate-500 truncate block">({d.variacoes.join(", ")})</span>}
                    </div>
                    <div className="flex gap-0.5 shrink-0">
                      <button onClick={() => moverDublador(i, -1)} disabled={i === 0} className="p-1 text-slate-500 hover:text-white disabled:opacity-30" data-testid={`calc-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => moverDublador(i, 1)} disabled={i === dubladores.length - 1} className="p-1 text-slate-500 hover:text-white disabled:opacity-30" data-testid={`calc-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
                      <button onClick={() => abrirEdicao(d)} className="p-1 text-slate-500 hover:text-amber-400" data-testid={`calc-edit-${i}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => removerDublador(d.id)} className="p-1 text-slate-500 hover:text-red-400" data-testid={`calc-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right - Input + Results */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4"><ClipboardList className="w-5 h-5 text-teal-400" /><span className="text-white font-medium">Entrada de Falas</span></div>
            <textarea
              value={entradaTexto} onChange={(e) => setEntradaTexto(e.target.value)}
              placeholder={`Cole a lista no formato:\nNOME    NÚMERO\n\nEx:\nLUCIANO    30\nMARIA    15\nBRENO, DAVID    10`}
              className="w-full bg-slate-900 border border-slate-600 text-white p-3 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm"
              rows={8} data-testid="calc-input-text"
            />
            <Button onClick={calcular} disabled={loading} className="bg-teal-600 hover:bg-teal-700 w-full mt-3 py-5" data-testid="calc-calcular-btn">
              <Calculator className="w-5 h-5 mr-2" />{loading ? "Calculando..." : "Calcular Orçamento"}
            </Button>
          </div>

          {resultados && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5" data-testid="calc-resultados">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white font-medium">Resultados</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={exportarExcel} className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8" data-testid="calc-export-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" /> Excel</Button>
                  <Button variant="outline" size="sm" onClick={exportarCSV} className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8" data-testid="calc-export-csv"><FileText className="w-4 h-4 mr-1" /> CSV</Button>
                </div>
              </div>

              <div className="text-center p-5 bg-slate-900 border border-slate-700 rounded-lg mb-4">
                <span className="block text-sm text-slate-400 mb-1">Total Geral</span>
                <span className="text-3xl font-bold text-teal-400 font-mono" data-testid="calc-total">R$ {resultados.total.toFixed(2)}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-4">
                {Object.entries(resultados.dubladores).map(([nome, valor]) => (
                  <div key={nome} className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-center" data-testid={`calc-res-${nome}`}>
                    <div className="text-xs text-slate-400 truncate mb-1">{nome}</div>
                    <div className="text-sm font-semibold text-emerald-400 font-mono">R$ {valor.toFixed(2)}</div>
                  </div>
                ))}
              </div>

              <div className="border border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="calc-table">
                  <thead><tr className="bg-slate-900"><th className="px-4 py-2 text-left text-slate-400">Dublador</th><th className="px-4 py-2 text-right text-slate-400">Valor</th></tr></thead>
                  <tbody>
                    {Object.entries(resultados.dubladores).map(([nome, valor]) => (
                      <tr key={nome} className="border-t border-slate-800 hover:bg-slate-900/50"><td className="px-4 py-2 text-white">{nome}</td><td className="px-4 py-2 text-right text-emerald-400 font-mono">R$ {valor.toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
